"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import clsx from "clsx";
import { companies, pillarMeta } from "@/data/companies";

type PillarKey = keyof typeof pillarMeta;

/* helpers */
function normalizeRegion(v: string) {
  const s = (v || "").toLowerCase().trim();
  if (!s) return "Global";
  if (/(mena|middle\s*east|gcc|uae|dubai|ksa|saudi|oman|qatar|bahrain)/.test(s)) return "MENA";
  if (/india|in\b/.test(s)) return "India";
  if (/global|world|intl|international/.test(s)) return "Global";
  return s.replace(/\s+/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}
function scoreYears(y: string) {
  const present = /present/i.test(y) ? 1000 : 0;
  const nums = [...y.matchAll(/\d{4}/g)].map((m) => parseInt(m[0], 10));
  const max = nums.length ? Math.max(...nums) : 0;
  const min = nums.length ? Math.min(...nums) : 0;
  return present + max + Math.round((max - min) / 2);
}
function shortPillarTitle(k: PillarKey) {
  // “PR”, “Content”, “Digital”
  return pillarMeta[k].title.split("&")[0].trim().split(" ")[0];
}
function tone(k: PillarKey) {
  return k === "content-and-seo" ? "green" : k === "pr" ? "blue" : "amber";
}

export default function WorkDirectory({ className = "" }: { className?: string }) {
  const [pillar, setPillar] = useState<PillarKey | "All">("All");
  const [q, setQ] = useState("");

  const pillarCounts = useMemo(() => {
    const m = {} as Record<PillarKey, number>;
    (Object.keys(pillarMeta) as PillarKey[]).forEach((k) => {
      m[k] = companies.filter((c) => c.pillar === k).length;
    });
    return m;
  }, []);

  const items = useMemo(() => {
    let list = companies.slice();
    if (pillar !== "All") list = list.filter((c) => c.pillar === pillar);
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(n) ||
          c.teaser.toLowerCase().includes(n) ||
          c.years.toLowerCase().includes(n)
      );
    }
    // keep a sensible order (recent-ish first)
    list.sort((a, b) => scoreYears(b.years) - scoreYears(a.years));
    return list;
  }, [pillar, q]);

  return (
    <div className={className}>
      {/* CTA for mobile - above filters */}
      <div className="flex md:hidden justify-center mb-4">
        <a href="https://calendly.com/varun-shetty2893/30min" target="_blank" rel="noopener noreferrer" className="btn btn-primary text-sm px-4 py-2 group relative overflow-hidden">
          <span className="relative z-10">Schedule a call</span>
        </a>
      </div>

      {/* Slim toolbar: pills only */}
      <div
        className={clsx(
          "rounded-2xl border bg-white/90 backdrop-blur-sm shadow-sm",
          "px-3 py-3 md:px-4 md:py-4"
        )}
      >
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          {/* Pill pills (single-select) */}
          <div className="flex flex-wrap gap-3 md:gap-3">
            <button
              type="button"
              onClick={() => setPillar("All")}
              className={clsx(
                "h-9 px-4 rounded-full border text-sm font-medium transition",
                pillar === "All"
                  ? "bg-neutral-900 text-white border-neutral-900"
                  : "bg-white text-neutral-900 border-neutral-200 hover:border-neutral-300"
              )}
            >
              All <span className="ml-1.5 opacity-70">({companies.length})</span>
            </button>

            {(Object.keys(pillarMeta) as PillarKey[]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setPillar(k)}
                className={clsx(
                  "h-9 px-4 rounded-full border text-sm font-medium transition",
                  pillar === k
                    ? "bg-neutral-900 text-white border-neutral-900"
                    : "bg-white text-neutral-900 hover:border-neutral-300",
                  tone(k) === "blue" && pillar !== k && "border-sky-200 text-sky-700",
                  tone(k) === "green" && pillar !== k && "border-emerald-200 text-emerald-700",
                  tone(k) === "amber" && pillar !== k && "border-amber-200 text-amber-700"
                )}
              >
                {shortPillarTitle(k)} <span className="ml-1.5 opacity-70">({pillarCounts[k]})</span>
              </button>
            ))}
          </div>

          {/* CTAs - only show on desktop */}
          <div className="hidden md:flex flex-col sm:flex-row gap-3">
            <a href="/contact" className="btn btn-primary text-sm px-4 py-2 group relative overflow-hidden">
              <span className="relative z-10">Write to me</span>
            </a>
            <a href="https://calendly.com/varun-shetty2893/30min" target="_blank" rel="noopener noreferrer" className="btn btn-primary text-sm px-4 py-2 group relative overflow-hidden">
              <span className="relative z-10">Schedule a call</span>
            </a>
          </div>
        </div>
      </div>

      {/* Results */}
      <ul className="mt-5 grid gap-4 md:grid-cols-3">
        {items.map((c) => (
          <li key={c.slug}>
            <Link
              href={`/work/${c.pillar}/${c.slug}`}
              className={clsx(
                "block rounded-2xl border border-neutral-200 bg-white p-5 transition",
                "hover:-translate-y-[2px] hover:shadow-[0_10px_28px_rgba(15,23,42,.08)] hover:border-neutral-300"
              )}
            >
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl border border-neutral-200 bg-white overflow-hidden flex items-center justify-center">
                  <Image
                    src={`/logos/${c.logo}`}
                    alt={`${c.name} logo`}
                    width={44}
                    height={44}
                    className="h-full w-full object-contain"
                  />
                </div>
                <div>
                  <h3 className="m-0 font-semibold leading-tight">{c.name}</h3>
                  <p className="mt-1 text-xs text-neutral-500">
                    {pillarMeta[c.pillar].title} • {normalizeRegion(c.region)} • {c.years}
                  </p>
                </div>
              </div>

              <p className="mt-3 text-neutral-600">{c.teaser}</p>
              <div className="mt-3 text-sm text-neutral-500">View work ↗</div>
            </Link>
          </li>
        ))}
      </ul>

      {items.length === 0 && (
        <div className="py-16 text-center text-neutral-500">
          No matches. Clear search or switch pillar.
        </div>
      )}
    </div>
  );
}